import { defineStore } from 'pinia';
import GeoMoverDB from '@/db/geoMoverDB';
import {
  applyTrackedRouteVersionToRecord,
  buildTrackedRouteVersionRecord,
  computeTrackVersionFingerprint
} from '@/utils/trackedRouteVersioning';

function clone (value) {
  try {
    return structuredClone(value);
  }
  catch {
    return JSON.parse(JSON.stringify(value));
  }
}

function readVersionsByTrackId (trackId) {
  return new Promise((resolve, reject) => {
    const tx = GeoMoverDB.db.transaction(['trackedRouteVersions'], 'readonly');
    const store = tx.objectStore('trackedRouteVersions');
    const index = store.index('trackId');
    const request = index.getAll(Number(trackId));

    request.onsuccess = () => {
      const versions = Array.isArray(request.result) ? request.result : [];
      versions.sort((a, b) => Number(b.versionNumber || 0) - Number(a.versionNumber || 0) || Number(b.createdAt || 0) - Number(a.createdAt || 0));
      resolve(versions);
    };
    request.onerror = () => reject(request.error);
  });
}

function updateVersionRecord (versionId, updates) {
  return new Promise((resolve, reject) => {
    const tx = GeoMoverDB.db.transaction(['trackedRouteVersions'], 'readwrite');
    const store = tx.objectStore('trackedRouteVersions');
    const getRequest = store.get(Number(versionId));

    getRequest.onsuccess = () => {
      const existing = getRequest.result;
      if (!existing) {
        reject(new Error('Track version not found.'));
        return;
      }

      const next = {
        ...existing,
        ...clone(updates)
      };
      store.put(next);
      tx.oncomplete = () => resolve(next);
    };
    getRequest.onerror = () => reject(getRequest.error);
    tx.onerror = () => reject(tx.error);
  });
}

function deleteVersionRecord (versionId) {
  return new Promise((resolve, reject) => {
    const tx = GeoMoverDB.db.transaction(['trackedRouteVersions'], 'readwrite');
    const store = tx.objectStore('trackedRouteVersions');
    store.delete(Number(versionId));
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
  });
}

function deleteVersionsByTrackId (trackId) {
  return new Promise((resolve, reject) => {
    const tx = GeoMoverDB.db.transaction(['trackedRouteVersions'], 'readwrite');
    const store = tx.objectStore('trackedRouteVersions');
    const index = store.index('trackId');
    const request = index.openCursor(IDBKeyRange.only(Number(trackId)));

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (!cursor) return;
      cursor.delete();
      cursor.continue();
    };
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
  });
}

export const useTrackedRouteVersionsStore = defineStore('trackedRouteVersions', {
  state: () => ({
    versionsByTrackId: {}
  }),

  actions: {
    async syncTrackVersionMetadata (trackId, overrides = {}) {
      const numericTrackId = Number(trackId);
      if (!Number.isFinite(numericTrackId)) throw new Error('Track ID is required.');

      const trackRecord = await GeoMoverDB.findOne('trackedRoutes', numericTrackId);
      if (!trackRecord) throw new Error('Track not found.');

      const versions = await this.getVersionsForTrack(numericTrackId, { force: true });
      const latestVersionNumber = versions.reduce((max, version) => Math.max(max, Number(version?.versionNumber) || 0), 0);
      const updatedTrack = {
        ...clone(trackRecord),
        versionCount: versions.length,
        latestVersionNumber,
        lastVersionSavedAt: Number(overrides.lastVersionSavedAt) || Number(trackRecord?.lastVersionSavedAt) || Date.now()
      };

      if (overrides.activeVersionFingerprint) {
        updatedTrack.activeVersionFingerprint = overrides.activeVersionFingerprint;
      }

      await GeoMoverDB.updateOne('trackedRoutes', updatedTrack, numericTrackId);
      return updatedTrack;
    },

    async getVersionsForTrack (trackId, options = {}) {
      const numericTrackId = Number(trackId);
      if (!Number.isFinite(numericTrackId)) return [];

      const { force = false } = options;
      if (!force && Array.isArray(this.versionsByTrackId[numericTrackId])) {
        return this.versionsByTrackId[numericTrackId];
      }

      const versions = await readVersionsByTrackId(numericTrackId);
      this.versionsByTrackId[numericTrackId] = versions;
      return versions;
    },

    async ensureInitialVersion (trackRecord) {
      const trackId = Number(trackRecord?.id);
      if (!Number.isFinite(trackId)) throw new Error('Track ID is required for version history.');

      const existing = await this.getVersionsForTrack(trackId, { force: true });
      if (existing.length > 0) return existing;

      const originalVersion = buildTrackedRouteVersionRecord(trackRecord, {
        trackId,
        versionNumber: 1,
        createdAt: Number(trackRecord?.date) || Date.now(),
        label: 'Original',
        source: 'original'
      });

      await GeoMoverDB.addOne('trackedRouteVersions', originalVersion);
      const persistedTrack = {
        ...clone(trackRecord),
        versionCount: 1,
        latestVersionNumber: 1,
        activeVersionFingerprint: trackRecord?.activeVersionFingerprint || computeTrackVersionFingerprint(trackRecord),
        lastVersionSavedAt: Number(trackRecord?.lastVersionSavedAt) || Number(trackRecord?.date) || Date.now()
      };
      await GeoMoverDB.updateOne('trackedRoutes', persistedTrack, trackId);
      return this.getVersionsForTrack(trackId, { force: true });
    },

    async saveTrackAsNewVersion (trackRecord, options = {}) {
      const trackId = Number(trackRecord?.id);
      if (!Number.isFinite(trackId)) throw new Error('Track ID is required to save a version.');

      const persistedTrack = await GeoMoverDB.findOne('trackedRoutes', trackId);
      if (!persistedTrack) throw new Error('Original track could not be loaded.');

      let versions = await this.getVersionsForTrack(trackId, { force: true });
      if (!versions.length) {
        versions = await this.ensureInitialVersion(persistedTrack);
      }

      const latestVersionNumber = versions.reduce((max, version) => Math.max(max, Number(version?.versionNumber) || 0), 0);
      const nextVersionNumber = latestVersionNumber + 1;
      const versionRecord = buildTrackedRouteVersionRecord(trackRecord, {
        trackId,
        versionNumber: nextVersionNumber,
        createdAt: Date.now(),
        label: options.label || `Version ${nextVersionNumber}`,
        source: options.source || 'manual',
        sourceMeta: options.sourceMeta || null
      });

      const newVersionId = await GeoMoverDB.addOne('trackedRouteVersions', versionRecord);
      const persistedVersion = { ...versionRecord, id: newVersionId };
      const totalVersionCount = versions.length + 1;

      const updatedTrack = {
        ...clone(trackRecord),
        versionCount: totalVersionCount,
        latestVersionNumber: nextVersionNumber,
        activeVersionFingerprint: versionRecord.snapshotFingerprint,
        lastVersionSavedAt: Date.now()
      };

      await GeoMoverDB.updateOne('trackedRoutes', updatedTrack, trackId);
      this.versionsByTrackId[trackId] = [persistedVersion, ...versions];

      return {
        version: persistedVersion,
        updatedTrack
      };
    },

    async activateVersion (trackId, versionId) {
      const numericTrackId = Number(trackId);
      const numericVersionId = Number(versionId);
      if (!Number.isFinite(numericTrackId) || !Number.isFinite(numericVersionId)) {
        throw new Error('Track ID and version ID are required.');
      }

      const trackRecord = await GeoMoverDB.findOne('trackedRoutes', numericTrackId);
      if (!trackRecord) throw new Error('Track not found.');

      const versionRecord = await GeoMoverDB.findOne('trackedRouteVersions', numericVersionId);
      if (!versionRecord || Number(versionRecord.trackId) !== numericTrackId) {
        throw new Error('Track version not found.');
      }

      const versions = await this.getVersionsForTrack(numericTrackId, { force: true });
      const updatedTrack = applyTrackedRouteVersionToRecord(trackRecord, versionRecord, {
        versionCount: versions.length,
        latestVersionNumber: versions.reduce((max, version) => Math.max(max, Number(version?.versionNumber) || 0), 0),
        lastVersionSavedAt: Date.now()
      });

      await GeoMoverDB.updateOne('trackedRoutes', updatedTrack, numericTrackId);
      return {
        version: versionRecord,
        updatedTrack
      };
    },

    async renameVersion (trackId, versionId, label) {
      const numericTrackId = Number(trackId);
      const nextLabel = String(label || '').trim();
      if (!Number.isFinite(numericTrackId)) throw new Error('Track ID is required.');
      if (!nextLabel) throw new Error('Version name cannot be empty.');

      const version = await GeoMoverDB.findOne('trackedRouteVersions', Number(versionId));
      if (!version || Number(version.trackId) !== numericTrackId) {
        throw new Error('Track version not found.');
      }

      await updateVersionRecord(versionId, { label: nextLabel });
      return this.getVersionsForTrack(numericTrackId, { force: true });
    },

    async deleteVersion (trackId, versionId) {
      const numericTrackId = Number(trackId);
      const numericVersionId = Number(versionId);
      if (!Number.isFinite(numericTrackId) || !Number.isFinite(numericVersionId)) {
        throw new Error('Track ID and version ID are required.');
      }

      const version = await GeoMoverDB.findOne('trackedRouteVersions', numericVersionId);
      if (!version || Number(version.trackId) !== numericTrackId) {
        throw new Error('Track version not found.');
      }

      const versions = await this.getVersionsForTrack(numericTrackId, { force: true });
      if (versions.length <= 1) {
        throw new Error('At least one version must remain.');
      }

      const trackRecord = await GeoMoverDB.findOne('trackedRoutes', numericTrackId);
      if (!trackRecord) throw new Error('Track not found.');

      const isCurrent = version.snapshotFingerprint === (trackRecord.activeVersionFingerprint || computeTrackVersionFingerprint(trackRecord));
      await deleteVersionRecord(numericVersionId);

      const remaining = await this.getVersionsForTrack(numericTrackId, { force: true });
      if (!remaining.length) {
        throw new Error('No versions remain after delete.');
      }

      if (isCurrent) {
        const fallback = remaining[0];
        const updatedTrack = applyTrackedRouteVersionToRecord(trackRecord, fallback, {
          versionCount: remaining.length,
          latestVersionNumber: remaining.reduce((max, item) => Math.max(max, Number(item?.versionNumber) || 0), 0),
          lastVersionSavedAt: Date.now()
        });
        await GeoMoverDB.updateOne('trackedRoutes', updatedTrack, numericTrackId);
        return {
          deletedVersionId: numericVersionId,
          activatedVersion: fallback,
          updatedTrack,
          versions: remaining
        };
      }

      const updatedTrack = await this.syncTrackVersionMetadata(numericTrackId, {
        activeVersionFingerprint: trackRecord.activeVersionFingerprint,
        lastVersionSavedAt: Date.now()
      });
      return {
        deletedVersionId: numericVersionId,
        activatedVersion: null,
        updatedTrack,
        versions: remaining
      };
    },

    async deleteVersionsForTrack (trackId) {
      const numericTrackId = Number(trackId);
      if (!Number.isFinite(numericTrackId)) return;
      await deleteVersionsByTrackId(numericTrackId);
      delete this.versionsByTrackId[numericTrackId];
    }
  }
});
